import { ColumnDef, getCoreRowModel, Row, useReactTable } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tile } from 'components/Layout/PageContainer';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { EmptyState } from 'components/EmptyState';
import { MeasureFormat } from 'components/measure-unit/MeasureFormat';
import { Flag } from 'components/Flag';
import { findIconForCategory } from 'pages/insights-pro/common/icons/CategoryIcons';
import { TbIcon } from 'components/Figma';
import { MeasureCode, useModelService } from 'services/model/model.service';
import { useDataEntriesInTables } from 'components/table/useDataEntriesInTables';
import { DataTableRenderer } from 'components/table/DataTableRenderer';
import { useProfitabilityRankedMovers } from './queryHooks';
import { RankedMoverRow, SupportedAttributeCode } from './types';
import { DataRequest } from 'hooks/useDataRequest';
import { cn } from 'utils/classNamesTailwind';

interface RankedProfitabilityMoversProps {
  requestData: DataRequest;
}

export function RankedProfitabilityMovers({ requestData }: RankedProfitabilityMoversProps) {
  const intl = useIntl();
  const { data, isLoading, isError, error } = useProfitabilityRankedMovers(requestData);

  if (isError) {
    return <Tile errorDetails={error} testId="ranked-movers-error" />;
  }

  if (isLoading || !data) {
    return (
      <Tile
        title={intl.formatMessage({ id: 'profitability.rankedMovers.title' })}
        additionalTitle={
          <span className="text-xs font-normal text-ash-700">
            {intl.formatMessage({ id: 'profitability.rankedMovers.subtitle' })}
          </span>
        }
      >
        <MetricsLoadingSpinner />
      </Tile>
    );
  }

  return (
    <Tile
      title={intl.formatMessage({ id: 'profitability.rankedMovers.title' })}
      additionalTitle={
        <span className="text-xs font-normal text-ash-700">
          {intl.formatMessage({ id: 'profitability.rankedMovers.subtitle' })}
        </span>
      }
      data-testid="ranked-profitability-movers"
    >
      <MoversSection sectionType="gainers" entries={data.gainers} />
      <div className="mt-6">
        <MoversSection sectionType="decliners" entries={data.decliners} />
      </div>
    </Tile>
  );
}

interface MoversSectionProps {
  sectionType: 'gainers' | 'decliners';
  entries: RankedMoverRow[];
}

function MoversSection({ sectionType, entries }: MoversSectionProps) {
  const intl = useIntl();
  const isGainers = sectionType === 'gainers';

  const titleId = isGainers ? 'profitability.rankedMovers.gainers' : 'profitability.rankedMovers.decliners';
  const emptyStateId = isGainers
    ? 'profitability.rankedMovers.emptyState.gainers'
    : 'profitability.rankedMovers.emptyState.decliners';

  return (
    <div data-testid={`ranked-movers-${sectionType}`}>
      <h3 className={cn('text-sm font-bold mb-2', isGainers ? 'text-green-700' : 'text-red-600')}>
        {intl.formatMessage({ id: titleId })}
      </h3>

      {entries.length === 0 ? (
        <EmptyState
          title={<span className="whitespace-pre-line">{intl.formatMessage({ id: emptyStateId })}</span>}
          testId={`ranked-movers-${sectionType}-empty`}
          imageClassName="block w-40"
          gapClassName="gap-8"
        />
      ) : (
        <MoversTable entries={entries} isGainers={isGainers} />
      )}
    </div>
  );
}

function useMoversColumns(isGainers: boolean): ColumnDef<RankedMoverRow>[] {
  const intl = useIntl();
  const { getMeasures } = useModelService();
  const { createMeasureColumn } = useDataEntriesInTables();

  const [cm2Val, cm2Rate, retRate] = getMeasures(['cm2ValExtExpOD', 'cm2RateExtExpOD', 'retQtyRateExpOD']);

  return [
    {
      id: 'entity',
      accessorFn: row => row.entityValue,
      header: intl.formatMessage({ id: 'profitability.rankedMovers.column.entity' }),
      enableSorting: false,
      cell: ({ row }) => {
        const { entityType, entityValue } = row.original;
        const name = resolveEntityName(intl, entityType, entityValue);
        const icon = resolveEntityIcon(entityType, entityValue);
        return (
          <div className="flex items-center gap-3">
            {icon}
            <span>{name}</span>
          </div>
        );
      },
    },
    {
      id: 'type',
      accessorFn: row => row.entityType,
      header: intl.formatMessage({ id: 'profitability.rankedMovers.column.type' }),
      enableSorting: false,
      cell: ({ row }) => intl.formatMessage({ id: `profitability.rankedMovers.entityType.${row.original.entityType}` }),
    },
    {
      id: 'deltaCm2',
      accessorFn: row => {
        const entry = row[cm2Val.code as MeasureCode];
        if (typeof entry === 'object' && entry !== null && 'value' in entry) {
          return entry.value - entry.compareValue;
        }
        return 0;
      },
      header: intl.formatMessage({ id: 'profitability.rankedMovers.column.deltaCm2' }),
      enableSorting: false,
      cell: ({ getValue }) => {
        const delta = getValue() as number;
        return (
          <span className={cn('font-semibold', isGainers ? 'text-green-700' : 'text-red-600')}>
            {isGainers ? '+ ' : '- '}
            <MeasureFormat value={Math.abs(delta)} unit={cm2Val.metric.unit} />
          </span>
        );
      },
    },
    {
      ...createMeasureColumn<RankedMoverRow>(cm2Rate),
      enableSorting: false,
    },
    {
      ...createMeasureColumn<RankedMoverRow>(retRate),
      enableSorting: false,
    },
  ];
}

interface MoversTableProps {
  entries: RankedMoverRow[];
  isGainers: boolean;
}

const NO_PAGINATION = { pageIndex: 0, pageSize: 100 };

function MoversTable({ entries, isGainers }: MoversTableProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const columns = useMoversColumns(isGainers);

  const table = useReactTable({
    data: entries,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
  });

  const handleRowClick = (row: Row<RankedMoverRow>) => {
    const { entityType, entityValue } = row.original;
    navigate(`/profitability/drilldown/${entityType}/${encodeURIComponent(entityValue)}${location.search}`);
  };

  return (
    <DataTableRenderer
      table={table}
      pagination={NO_PAGINATION}
      onRowClick={handleRowClick}
      hidePagination
      testIdPrefix={`ranked-movers-${isGainers ? 'gainers' : 'decliners'}`}
    />
  );
}

function resolveEntityName(
  intl: ReturnType<typeof useIntl>,
  entityType: SupportedAttributeCode,
  entityValue: string
): string {
  switch (entityType) {
    case 'prdCategory':
      return intl.formatMessage({
        id: `product.categories.${entityValue}`,
        defaultMessage: entityValue,
      });
    case 'chnCountry':
      return intl.formatDisplayName(entityValue?.toUpperCase(), { type: 'region' }) ?? entityValue;
    case 'chnMarketplace':
    default:
      return entityValue;
  }
}

function resolveEntityIcon(entityType: SupportedAttributeCode, entityValue: string): React.ReactNode {
  switch (entityType) {
    case 'prdCategory':
      return findIconForCategory(entityValue, 24);
    case 'chnCountry':
      return <Flag countryCode={entityValue} tooltip={entityValue} />;
    case 'chnMarketplace':
      return <TbIcon name="ShoppingCart" size="xl" />;
    default:
      return null;
  }
}
